
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  MapPin,
  Clock,
  CheckCircle2,
  Circle
} from "lucide-react";
import { BookingDetail } from "@/hooks/useBooking";

interface BookingServiceOverviewProps {
  booking: BookingDetail;
  statusBadge: React.ReactNode;
  actualCheckInTime: string | null;
  actualCheckOutTime: string | null;
  isCheckedIn: boolean;
  isCompleted: boolean;
}

export const BookingServiceOverview = ({ 
  booking, 
  statusBadge,
  actualCheckInTime,
  actualCheckOutTime,
  isCheckedIn,
  isCompleted
}: BookingServiceOverviewProps) => {
  return (
    <Card className="border shadow-sm bg-card">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-2xl text-foreground mb-2">{booking.service_name}</CardTitle>
            <div className="flex items-center text-muted-foreground">
              <Clock className="h-4 w-4 mr-2" />
              <span>{booking.duration_minutes} Minutes</span>
            </div>
          </div>
          {statusBadge}
        </div>
      </CardHeader>
      <CardContent>
        {/* Location */}
        <div className="space-y-3">
          <div className="flex items-center text-foreground font-medium">
            <MapPin className="h-5 w-5 mr-2 text-muted-foreground" />
            Location
          </div>
          <div className="ml-7 space-y-1">
            <p className="font-medium text-foreground">{booking.location}</p>
            <p className="text-muted-foreground text-sm">{booking.address}</p>
          </div>
        </div>

        <Separator className="my-6" />

        {/* Session Progress */}
        <div className="space-y-4">
          <h3 className="font-semibold text-foreground flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-green-600" />
            Session Progress
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {isCheckedIn || isCompleted ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-3" />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground mr-3" />
                )}
                <span className={isCheckedIn || isCompleted ? "text-green-700 font-medium" : "text-muted-foreground"}>
                  Check-in
                </span>
              </div>
              <span className="text-sm text-muted-foreground">
                {actualCheckInTime || "Pending"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {isCompleted ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-3" />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground mr-3" />
                )}
                <span className={isCompleted ? "text-green-700 font-medium" : "text-muted-foreground"}>
                  Check-out
                </span>
              </div>
              <span className="text-sm text-muted-foreground">
                {actualCheckOutTime || "Pending"}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
